import type { BackendErrorType } from './types';

/**
 * Общая реакция на контрактные ошибки сессии.
 *
 * Раздел 6 FRONTEND_API.md описывает реакцию не для конкретной ручки, а для
 * любого ответа: сессия может отвалиться на запросе курса или анкеты так же,
 * как на продлении. Поэтому реакция живёт здесь, в одной точке, и вызывается
 * из низкоуровневого клиента — иначе каждый стор повторял бы одни и те же
 * проверки и рано или поздно один из них забыл бы это сделать.
 *
 * Обработчики регистрирует authStore: клиент не должен знать про состояние
 * приложения, а стор не должен оборачивать каждый вызов вручную.
 */
export interface SessionGuardHandlers {
  /** 401: сессия недействительна. `replaced` — вход выполнен с другого устройства. */
  onSessionLost(replaced: boolean): void;
  /** 403 REGISTRATION_REQUIRED: пользователь ещё не завершил регистрацию. */
  onRegistrationRequired(): void;
  /** 409 REGISTRATION_ALREADY_COMPLETED: состояние устарело, нужно перечитать сессию. */
  onRegistrationCompleted(): void;
}

let handlers: SessionGuardHandlers | null = null;

export function registerSessionGuard(next: SessionGuardHandlers): void {
  handlers = next;
}

/** Только для тестов: снять обработчики между сценариями. */
export function clearSessionGuard(): void {
  handlers = null;
}

/**
 * Ручки входа и продления исключены: их ответы authStore разбирает сам,
 * а обработка 409 здесь снова дёргает `session` и зациклила бы вызов.
 */
const SELF_HANDLED_PATHS = new Set(['/api/miniapp/auth', '/api/miniapp/session']);

export function notifySessionGuard(
  path: string,
  status: number | undefined,
  type: BackendErrorType | undefined
): void {
  if (!handlers || SELF_HANDLED_PATHS.has(path)) return;

  if (type === 'MINIAPP_SESSION_REPLACED') {
    handlers.onSessionLost(true);
    return;
  }
  if (type === 'MINIAPP_SESSION_NOT_FOUND' || status === 401) {
    handlers.onSessionLost(false);
    return;
  }
  if (type === 'REGISTRATION_REQUIRED') {
    handlers.onRegistrationRequired();
    return;
  }
  if (type === 'REGISTRATION_ALREADY_COMPLETED') {
    handlers.onRegistrationCompleted();
  }
}
